import { useEffect, useRef, useState } from 'react';

const DELAY = 500;

function isKakaoTalk() {
  return /KAKAOTALK/i.test(navigator.userAgent);
}

export default function AwakeHandoff() {
  const query = new URLSearchParams(window.location.search);
  const successUrl = query.get('successUrl') || '';
  const failUrl = query.get('failUrl') || '';
  const kakao = useRef(isKakaoTalk());

  const [done, setDone] = useState(false);
  const [dots, setDots] = useState('');
  const [error, setError] = useState('');
  const didRun = useRef(false);

  // ... dot animation (for KakaoTalk in-app only)
  useEffect(() => {
    if (!kakao.current || done || error) return;
    const id = setInterval(() => {
      setDots(d => d.length >= 3 ? '' : d + '.');
    }, 300);
    return () => clearInterval(id);
  }, [done, error]);

  useEffect(() => {
    if (didRun.current) return;
    didRun.current = true;

    if (!successUrl) {
      setError('이동 주소가 없습니다.');
      return;
    }

    if (!kakao.current) {
      // Not KakaoTalk: Redirect immediately
      window.location.replace(successUrl);
      return;
    }

    // KakaoTalk: Wait 500ms with animation, then open external & close in-app
    const timer = setTimeout(() => {
      try {
        // Open external browser
        window.location.href = 'kakaotalk://web/openExternal?url=' + encodeURIComponent(successUrl);
        
        // Close in-app browser using a different method to avoid conflict with the first location.href
        setTimeout(() => {
          const a = document.createElement('a');
          a.href = 'kakaotalk://inappbrowser/close';
          a.click();
        }, 500); // Increased delay slightly
        
        setDone(true);
      } catch {
        setError('앱으로 이동하지 못했습니다.');
      }
    }, DELAY);

    return () => clearTimeout(timer);
  }, [successUrl]);

  const s: Record<string, React.CSSProperties> = {
    wrap: {
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', minHeight: '100dvh', padding: '32px 24px',
      fontFamily: 'sans-serif', gap: 16, textAlign: 'center',
    },
    icon: { fontSize: 48, lineHeight: 1 },
    title: { fontSize: 20, fontWeight: 700, margin: 0 },
    desc: { fontSize: 14, color: 'var(--user-text-muted)', margin: 0 },
    dots: {
      fontSize: 22, color: '#3A57E8', letterSpacing: 4,
      minWidth: 36, display: 'inline-block', textAlign: 'left',
    },
    btn: {
      display: 'inline-block', marginTop: 8,
      padding: '12px 28px', borderRadius: 8, border: 'none', cursor: 'pointer',
      fontSize: 15, fontWeight: 600, textDecoration: 'none',
    },
    primary: { background: '#3A57E8', color: '#fff' },
    secondary: { background: 'var(--user-surface-raised)', color: 'var(--user-text)' },
  };

  if (error) {
    return (
      <main style={s.wrap}>
        <p style={s.icon}>⚠️</p>
        <p style={s.title}>이동에 실패했습니다</p>
        <p style={s.desc}>{error}</p>
        {successUrl && (
          <button
            style={{ ...s.btn, ...s.primary }}
            onClick={() => {
              if (kakao.current) {
                window.location.href = 'kakaotalk://web/openExternal?url=' + encodeURIComponent(successUrl);
                setTimeout(() => {
                  const a = document.createElement('a');
                  a.href = 'kakaotalk://inappbrowser/close';
                  a.click();
                }, 500);
              } else {
                window.location.replace(successUrl);
              }
            }}
          >
            다시 시도
          </button>
        )}
        {failUrl && (
          <button style={{ ...s.btn, ...s.secondary }} onClick={() => window.location.replace(failUrl)}>
            AWAKE로 돌아가기
          </button>
        )}
        <a href="/" style={{ ...s.btn, ...s.secondary }}>팬버드 홈으로</a>
      </main>
    );
  }

  // UI (Only visible in KakaoTalk)
  return (
    <main style={s.wrap}>
      <p style={s.icon}>✓</p>
      <p style={s.title}>팬버드 로그인 완료</p>
      {done
        ? (
          <>
            <p style={s.desc}>AWAKE 앱으로 이동 중입니다.<br />자동으로 이동하지 않으면 아래 버튼을 눌러주세요.</p>
            <button
              style={{ ...s.btn, ...s.primary }}
              onClick={() => {
                window.location.href = 'kakaotalk://web/openExternal?url=' + encodeURIComponent(successUrl);
                setTimeout(() => {
                  const a = document.createElement('a');
                  a.href = 'kakaotalk://inappbrowser/close';
                  a.click();
                }, 500);
              }}
            >
              AWAKE로 이동
            </button>
          </>
        ) : (
          <p style={s.desc}>
            이동 중<span style={s.dots}>{dots}</span>
          </p>
        )
      }
      <a href="/" style={{ ...s.btn, ...s.secondary }}>팬버드 홈으로</a>
    </main>
  );
}
